import { useEffect, useState } from "react";

// Streams a status while the operator waits. Stages are time-driven (no real
// signal from backend during pending) but match the actual scraper boot
// sequence so the operator has something concrete to read.
//
// The final stage is platform-specific: Telegram (telethon) sends a login CODE
// to the account that the operator types in — it never shows a "pairing code".
// WhatsApp surfaces a pairing code. Using the generic "pairing code" wording for
// Telegram is misleading (it's the exact symptom that masked a stuck session).
function stagesFor(platform: string): { atSec: number; label: string }[] {
  if (platform === "telegram") {
    return [
      { atSec: 0, label: "Connecting to scraper VM…" },
      { atSec: 3, label: "Spawning the Telegram client (telethon)…" },
      { atSec: 10, label: "Connecting to Telegram…" },
      { atSec: 18, label: "Asking Telegram to send the login code…" },
    ];
  }
  if (platform === "whatsapp") {
    return [
      { atSec: 0, label: "Connecting to scraper VM…" },
      { atSec: 3, label: "Spawning the avatar's client (Chrome cold-start)…" },
      { atSec: 10, label: "Loading WhatsApp Web…" },
      { atSec: 18, label: "Requesting pairing code from WhatsApp…" },
    ];
  }
  return [
    { atSec: 0, label: "Connecting to scraper VM…" },
    { atSec: 3, label: "Spawning the avatar's client…" },
    { atSec: 10, label: "Connecting to the platform…" },
    { atSec: 18, label: "Requesting a login challenge from the platform…" },
  ];
}

export function PendingStep({
  avatarName,
  platform,
  onCancel,
}: {
  avatarName: string;
  platform: string;
  onCancel: () => void;
}) {
  const [elapsed, setElapsed] = useState(0);
  const [agentSlow, setAgentSlow] = useState(false);
  useEffect(() => {
    const tick = setInterval(() => setElapsed((s) => s + 1), 1000);
    const slow = setTimeout(() => setAgentSlow(true), 25000);
    return () => {
      clearInterval(tick);
      clearTimeout(slow);
    };
  }, []);

  const currentStage =
    [...stagesFor(platform)].reverse().find((s) => elapsed >= s.atSec)?.label ??
    "Connecting to scraper VM…";

  return (
    <div style={{ padding: 24, textAlign: "center" }}>
      <div className="iris-spinner" />
      <div style={{ marginTop: 16, fontSize: 14, fontWeight: 500 }}>
        Relinking <span style={{ fontFamily: "ui-monospace" }}>{avatarName}</span> ({platform})
      </div>
      <div style={{ marginTop: 8, color: "var(--iris-fg-3)" }}>{currentStage}</div>
      <div style={{ marginTop: 4, fontSize: 11, color: "var(--iris-fg-4)" }}>
        Elapsed: {elapsed}s
      </div>
      {agentSlow && (
        <div style={{ marginTop: 12, color: "var(--iris-warn, #c97a00)", fontSize: 12 }}>
          Taking longer than usual — relink agent or scraper may be slow. You can keep waiting or
          cancel and retry.
        </div>
      )}
      <button type="button" className="iris-btn" style={{ marginTop: 16 }} onClick={onCancel}>
        Cancel
      </button>
    </div>
  );
}
